/**
 * The overhit clamp (presentation only, client-side).
 *
 * The sim emits `combat-hit` carrying the damage that LANDED (combat.ts —
 * defense is hit-frequency only, so a hit applies its full rolled number).
 * When that number exceeds the target's remaining HP, the sim clamps the
 * bar at 0 but the event carries the full roll — without this clamp a 10
 * off a 1-HP mob would splat and chat "10" while the bar moved by 1. The
 * number the party sees must match the bar: show at most what the target
 * had left when the swing connected.
 *
 * Input: a snapshot of every LIVING entity's HP taken from the
 * PRE-tick observation (the hit tick's own observation already shows
 * post-hit HP) + the tick's wire events. Output: the same events with
 * each `combat-hit`'s `damage` clamped. Hits on the SAME target within
 * one tick apply sequentially against the running remainder (a sweep and
 * a swing sum to at most the target's pre-tick HP, never more). A target
 * absent from the snapshot (spawned this tick, or a hero raised by a
 * respawn this tick) shows its rolled number — the clamp never invents.
 *
 * `combat-block` is deliberately untouched: its number is the WOULD-BE
 * value by design (the gray "it rolled 19, did nothing" Barricade splat).
 * `combat-miss` carries no damage.
 *
 * This never feeds back into the sim: the client renders, it never
 * decides (AGENTS §12). The server's event stays authoritative; only the
 * displayed number changes.
 */

/** The entity fields the snapshot needs (both ObservedEnemy and
 *  ObservedPlayer carry them). */
export interface HpEntity {
  id: number;
  alive: boolean;
  hp: number;
}

/** Each LIVING entity's HP, keyed the way the effect layer keys entities
 *  (`e3` / `p1`). Dead entities are absent — a hit on a corpse is not a
 *  sim event, and a hero raised by this tick's respawn must read
 *  "unknown", not "0 HP". */
export function hpBeforeSnapshot(
  enemies: readonly HpEntity[],
  players: readonly HpEntity[],
): Map<string, number> {
  const m = new Map<string, number>();
  for (const e of enemies) if (e.alive) m.set(`e${e.id}`, e.hp);
  for (const p of players) if (p.alive) m.set(`p${p.id}`, p.hp);
  return m;
}

/** The wire shape of a landed hit (defensive narrowing at the boundary,
 *  the same discipline as effects.ts' isCombatEvent). */
function isCombatHit(raw: unknown): raw is {
  type: "combat-hit";
  targetIsEnemy: boolean;
  targetId: number;
  damage: number;
} {
  if (!raw || typeof raw !== "object") return false;
  const e = raw as Record<string, unknown>;
  return (
    e.type === "combat-hit" &&
    typeof e.targetIsEnemy === "boolean" &&
    typeof e.targetId === "number" &&
    typeof e.damage === "number"
  );
}

/** Clamp each `combat-hit` to at most the target's pre-tick HP (see the
 *  module doc for the semantics). Passes every other event through
 *  unchanged; never mutates the input. */
export function clampHitDamage(
  events: readonly unknown[],
  hpBefore: Map<string, number>,
): unknown[] {
  const running = new Map<string, number>();
  return events.map((e) => {
    if (!isCombatHit(e)) return e;
    const k = (e.targetIsEnemy ? "e" : "p") + e.targetId;
    const start = hpBefore.get(k);
    if (start === undefined) return e; // unknown pre-tick: show the roll
    const remaining = running.get(k) ?? start;
    const d = Math.min(e.damage, Math.max(0, remaining));
    running.set(k, remaining - d);
    return d === e.damage ? e : { ...e, damage: d };
  });
}
